/**
 * @module queryParams
 *
 * Lightweight replacement for the `query-string` package.
 *
 * Supports the single format the app uses throughout: arrays serialized
 * as comma-separated values in a single query parameter, e.g.
 *   groupIds=1,2,3   (not groupIds=1&groupIds=2)
 *
 * This is a drop-in replacement for:
 *   queryString.parse(search, { arrayFormat: 'comma' })
 *   queryString.stringify(obj, { arrayFormat: 'comma' })
 *
 * @example
 * import { parseQuery, stringifyQuery } from '../utils/queryParams';
 *
 * const params = parseQuery(location.search);
 * // { scope: 'AGENCY', groupIds: ['1', '2'] }
 *
 * const search = stringifyQuery({ ...params, page: '2' });
 * // 'scope=AGENCY&groupIds=1%2C2&page=2'
 */

/** The parsed shape returned by parseQuery. */
export type ParsedQuery = Record<
  string,
  string | string[] | null | (string | null)[]
>;

/**
 * Parses a URL search string into a record.
 *
 * Values that contain commas are returned as string arrays.
 * Missing values (bare `?key`) are returned as `null`.
 * All other values are returned as strings.
 *
 * Equivalent to: queryString.parse(search, { arrayFormat: 'comma' })
 */
export function parseQuery(search: string): ParsedQuery {
  const result: ParsedQuery = {};
  if (!search) return result;

  const params = new URLSearchParams(search);
  params.forEach((value, key) => {
    if (value === '') {
      result[key] = null;
    } else if (value.includes(',')) {
      result[key] = value.split(',').map((v) => v.trim());
    } else {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Serializes an object to a URL search string.
 *
 * Arrays are encoded as comma-separated values in a single parameter.
 * null/undefined values are omitted.
 *
 * Equivalent to: queryString.stringify(obj, { arrayFormat: 'comma' })
 */
export function stringifyQuery(obj: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, (value as (string | number)[]).map(String).join(','));
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      params.set(key, String(value));
    }
  }
  return params.toString();
}
